import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {PaymentState, FetchPaymentByIdResponse} from './types'
import {
  fetchPaymentById,
  fetchPayments,
  fetchPaymentsScope,
  probePaymentsDateRangeSupport,
  createPayment,
  updatePayment,
  deletePayment,
} from './service'
import {serializePaymentsScopeKey} from './scopeCache'

const initialState: PaymentState = {
  paymentForEdit: null,
  payments: [],
  total: 0,
  totalPages: 0,
  progress: 0,
  listLoading: false,
  actionLoading: false,
  error: null,
  scopePayments: [],
  scopePaymentsKey: null,
  scopePaymentsRequestedKey: null,
  scopePaymentsLoading: false,
  scopePaymentsRefreshing: false,
  scopePaymentsError: null,
  scopePaymentsLoadedAt: null,
  scopePaymentsStale: false,
  dateRangeFilterSupported: null,
  listVersion: 0,
  listRefreshing: false,
  listFetchRequestId: null,
  scopeFetchRequestId: null,
}

function markScopeStale(state: PaymentState) {
  state.scopePaymentsStale = true
  state.scopePaymentsLoadedAt = null
}

function bumpListAfterMutation(state: PaymentState) {
  state.listVersion += 1
  markScopeStale(state)
}

const paymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {
    handleRequestProgress: (state: PaymentState, action: any) => {
      const {progress} = action.payload
      state.progress = progress
    },
    resetPayments: (state: PaymentState) => {
      state.payments = []
      state.total = 0
      state.totalPages = 0
    },
    bumpListVersion: (state: PaymentState) => {
      bumpListAfterMutation(state)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPayments.pending, (state: PaymentState, action) => {
      state.listFetchRequestId = action.meta.requestId
      state.error = null
      if (state.payments.length > 0) {
        state.listRefreshing = true
        state.listLoading = false
      } else {
        state.listLoading = true
        state.listRefreshing = false
      }
    })
    builder.addCase(fetchPayments.rejected, (state: PaymentState, action) => {
      if (action.meta.requestId !== state.listFetchRequestId) return
      const payload = action.payload as {error?: string} | undefined
      state.error = payload?.error || 'message'
      state.listLoading = false
      state.listRefreshing = false
    })
    builder.addCase(fetchPayments.fulfilled, (state: PaymentState, action) => {
      if (action.meta.requestId !== state.listFetchRequestId) return
      const {payments, total, totalPages} = action.payload
      state.payments = payments
      state.total = total
      state.totalPages = totalPages
      state.listLoading = false
      state.listRefreshing = false
    })

    builder.addCase(probePaymentsDateRangeSupport.fulfilled, (state: PaymentState, action) => {
      state.dateRangeFilterSupported = action.payload.supported
    })
    builder.addCase(probePaymentsDateRangeSupport.rejected, (state: PaymentState) => {
      state.dateRangeFilterSupported = false
    })

    builder.addCase(fetchPaymentsScope.pending, (state: PaymentState, action) => {
      const key = serializePaymentsScopeKey(action.meta.arg || {})
      state.scopeFetchRequestId = action.meta.requestId
      state.scopePaymentsRequestedKey = key
      state.scopePaymentsError = null
      const hasMatchingCache =
        state.scopePaymentsKey === key &&
        (state.scopePaymentsLoadedAt != null || state.scopePayments.length > 0)
      if (hasMatchingCache) {
        state.scopePaymentsRefreshing = true
        state.scopePaymentsLoading = false
      } else {
        state.scopePaymentsLoading = true
        state.scopePaymentsRefreshing = false
      }
    })
    builder.addCase(fetchPaymentsScope.rejected, (state: PaymentState, action) => {
      if (action.meta.requestId !== state.scopeFetchRequestId) return
      const payload = action.payload as {error?: string} | undefined
      state.scopePaymentsLoading = false
      state.scopePaymentsRefreshing = false
      state.scopePaymentsRequestedKey = null
      state.scopePaymentsError = payload?.error || 'load_failed'
    })
    builder.addCase(fetchPaymentsScope.fulfilled, (state: PaymentState, action) => {
      if (action.meta.requestId !== state.scopeFetchRequestId) return
      const {payments, key, loadedAt} = action.payload
      state.scopePayments = payments
      state.scopePaymentsKey = key
      state.scopePaymentsLoadedAt = loadedAt
      state.scopePaymentsLoading = false
      state.scopePaymentsRefreshing = false
      state.scopePaymentsStale = false
      state.scopePaymentsError = null
      state.scopePaymentsRequestedKey = null
    })

    builder.addCase(fetchPaymentById.pending, (state: PaymentState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(
      fetchPaymentById.rejected,
      (state: PaymentState, action: PayloadAction<any>) => {
        const {error} = action.payload
        state.error = error
        state.actionLoading = false
      }
    )
    builder.addCase(
      fetchPaymentById.fulfilled,
      (state: PaymentState, action: PayloadAction<FetchPaymentByIdResponse>) => {
        const {payment} = action.payload
        state.paymentForEdit = payment
        state.actionLoading = false
      }
    )

    builder.addCase(createPayment.pending, (state: PaymentState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(createPayment.rejected, (state: PaymentState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.actionLoading = false
    })
    builder.addCase(createPayment.fulfilled, (state: PaymentState) => {
      state.actionLoading = false
      bumpListAfterMutation(state)
    })

    builder.addCase(updatePayment.pending, (state: PaymentState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(updatePayment.rejected, (state: PaymentState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.actionLoading = false
    })
    builder.addCase(updatePayment.fulfilled, (state: PaymentState) => {
      state.actionLoading = false
      bumpListAfterMutation(state)
    })

    builder.addCase(deletePayment.pending, (state: PaymentState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(deletePayment.rejected, (state: PaymentState, action: PayloadAction<any>) => {
      const {error} = action.payload
      state.error = error
      state.actionLoading = false
    })
    builder.addCase(deletePayment.fulfilled, (state: PaymentState, action: PayloadAction<any>) => {
      const {payment} = action.payload
      if (payment?._id) {
        state.payments = state.payments.filter((p) => p._id !== payment._id)
        state.scopePayments = state.scopePayments.filter((p) => p._id !== payment._id)
      }
      state.actionLoading = false
      bumpListAfterMutation(state)
    })
  },
})

export const actions = paymentSlice.actions
export default paymentSlice.reducer
